import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { CaretDown, UserCircle, SignOut, ShieldCheck, List, Key } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

// Labels follow global MRP/ERP naming conventions (SAP/Oracle/Infor-style
// module names) - renamed Feb 2026 as part of the "Production Control" rebrand.
// Reorganized Sep 4 2026, user's explicit ask: menu groups now match real
// ERP module boundaries (Procurement vs Supplier/Vendor lifecycle vs true
// Master Data vs Administration) instead of dumping supplier-portal admin
// actions under "Master Data" just because that's where they happened to
// land first. Routes are unchanged; only labels/grouping changed. `page`
// matches backend/auth_service.py's PAGE_CATALOG keys - a tab only
// renders if the signed-in user has that page.
const TABS = [
  { to: "/", label: "BOM Management", testId: "nav-bom-explorer", page: "bom_explorer" },
];

// Sep 15 2026, user's explicit ask: both Production Confirmation pages
// grouped under one "Production" dropdown (replaces the earlier direct
// top-level tabs, which combined with everything else overflowed the nav
// at common desktop widths).
const PRODUCTION_SUBTABS = [
  { to: "/production-confirmation", label: "Production Confirmation", testId: "nav-production-production-confirmation", page: "production_confirmation" },
  // Sep 15 2026, user's explicit ask: reporting counterpart of Production
  // Confirmation (transaction-level history) split into its own page -
  // reuses the same "production_confirmation" access right.
  { to: "/production-confirmation/confirmed-report", label: "Confirmed Production", testId: "nav-production-confirmed-production", page: "production_confirmation" },
  { to: "/admin/production-confirmation-test", label: "Production Confirmation (Test)", testId: "nav-production-production-confirmation-test", page: "production_confirmation_test" },
  // Oct 2026: shop-floor daily consumption + weekly count for chemicals and
  // consumables - own grantable right (see PAGE_CATALOG's "consumption_register").
  { to: "/consumption-register", label: "Consumption Register", testId: "nav-production-consumption-register", page: "consumption_register" },
  // Oct 27 2026, user's spec: the quality team's screen for scrap raised on a
  // production model the Admin assigned Quality Activity to.
  { to: "/quality-decision", label: "Quality Decision", testId: "nav-production-quality-decision", anyOf: ["quality_decision", "production_confirmation"] },
];

const INVENTORY_SUBTABS = [
  { to: "/inventory", label: "Stock Overview", testId: "nav-inventory-stock-overview", page: "inventory" },
  // Oct 3 2026, user's explicit ask: "Inter Plant Stock Transfer" and
  // "Inbound STO Receipt" removed from the app (nav entries + routes, see
  // App.js) - backend endpoints and access checkboxes untouched.
  // Aug 2026, user's explicit ask: links out to the existing Store
  // Approval screen (public/unauthenticated route, unchanged) - just a
  // shortcut into it from the main nav, no backend permission change.
  // Sep 5 2026, user's explicit ask: relabeled "Goods Issue" -> "Store
  // Goods Issue" for clarity (still the same store_approval page/route).
  { to: "/storeapproval", label: "Store Goods Issue", testId: "nav-inventory-goods-issue", page: "store_approval" },
  // Sep 24 2026, user's explicit ask: cross-cutting read-only ledger of
  // every SAP Goods Movement across STO Relocation, Store Issue, and
  // Store Return - own grantable right, own page.
  // Jun 2026, user's explicit ask ("allow movement audit to all user"): no
  // `page` key here = visible to every signed-in user (see the
  // `!t.page || hasPageAccess(...)` filter below).
  { to: "/inventory/movement-audit-trail", label: "Movement Audit Trail", testId: "nav-inventory-movement-audit-trail" },
  // Sep 25 2026, user's explicit ask: aggregated "In-Process Qty by
  // Stage" view, derived from Production Confirmation checkpoints - own
  // grantable right, own page.
  { to: "/inventory/wip-by-stage", label: "WIP by Stage", testId: "nav-inventory-wip-by-stage", page: "wip_by_stage" },
  // Oct 21 2026, user's approved spec: count BOM/consumable stock per site
  // and warehouse, submit the differences for admin confirmation.
  { to: "/physical-inventory", label: "Physical Inventory", testId: "nav-inventory-physical-inventory", page: "physical_inventory" },
];

// Sep 24 2026: app trimmed to a Production/Inventory-only tool - Master
// Data now keeps only the true reference table (Component Master).
// Jun 2026, user's ask: "Why is the NPD menu still under inventory
// management? I wanted it separate." - NPD is its own top-level menu with the
// three NPD action items. Each item deep-links to its section of the NPD page.
// Oct 2026 context: the NPD team raises its own store requests (site + purpose
// + item codes) under the npd_store_request right; the two tooling approval
// rights open ONLY the approvals item, so management never needs the NPD right.
const NPD_SUBTABS = [
  { to: "/npd-requests/store", label: "Store Requests and Approvals", testId: "nav-npd-store-requests", page: "npd_store_request" },
  { to: "/npd-requests/tooling", label: "Tooling Budget Requests", testId: "nav-npd-tooling-requests", page: "npd_store_request" },
  { to: "/npd-requests/approvals", label: "Tooling Budget Approvals", testId: "nav-npd-tooling-approvals", anyOf: ["npd_tooling_head_approval", "npd_tooling_management_approval"] },
];

const MASTER_DATA_SUBTABS = [
  { to: "/admin", label: "Component Master", testId: "nav-master-data-component-master", page: "admin" },
];

const ADMINISTRATION_SUBTABS = [
  { to: "/admin/sap-write", label: "SAP Write", testId: "nav-administration-sap-write", page: "admin_sap_write" },
  { to: "/admin/bom-copy", label: "Copy Existing BOM", testId: "nav-administration-bom-copy", page: "admin_bom_copy" },
  { to: "/admin/boo", label: "Bill of Operations", testId: "nav-administration-boo", page: "admin_boo" },
  { to: "/admin/routing-builder", label: "Routing Builder", testId: "nav-administration-routing-builder", page: "admin_routing_builder" },
  // Oct 2026, user's approved spec: machines (PRESS-01, CNC-02...) behind
  // the external Shop Floor app's first screen - own grantable right.
  { to: "/admin/machine-master", label: "Machine Master", testId: "nav-administration-machine-master", page: "machine_master" },
  // Sep 29 2026, user's explicit ask ("restricted to Administrator role")
  // - roleOnly (admin/super_admin), deliberately NOT a PAGE_CATALOG-
  // assignable right like the tabs above (matches ProtectedRoute's own
  // superAdminOnly gate on this route).
  { to: "/admin/cost-center-consumption", label: "Cost Center Consumption", testId: "nav-administration-cost-center-consumption", roleOnly: true },
  { to: "/admin/byproduct-exemption", label: "Byproduct Exemption", testId: "nav-administration-byproduct-exemption", roleOnly: true },
  // Oct 31 2026: items with their own SAP BOM that are really bought from a
  // vendor at a given plant, so they still get a Store Request - admin only.
  { to: "/admin/store-issued-items", label: "Store-Issued Items", testId: "nav-administration-store-issued-items", roleOnly: true },
  // Oct 2026: master data for the Consumption Register (departments,
  // processes, mapped materials) - admin/super_admin only, same roleOnly
  // pattern as the two entries above.
  { to: "/admin/consumption-register-setup", label: "Consumption Register Setup", testId: "nav-administration-consumption-register-setup", roleOnly: true },
  // Oct 27 2026: which production models route their scrap through Quality
  // Decision - admin only, same roleOnly pattern as the entries above.
  { to: "/admin/quality-activity", label: "Quality Activity", testId: "nav-administration-quality-activity", roleOnly: true },
  { to: "/admin/sap-credentials", label: "SAP Credentials", testId: "nav-administration-sap-credentials", roleOnly: true },
];

export const NavTabs = () => {
  const { pathname } = useLocation();
  const { user, hasPageAccess, logout } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  // Oct 26 2026: a shop-floor username login was granted the Consumption
  // Register and nothing else, so the few entries open to every signed-in
  // user (Movement Audit Trail) stay hidden from them as well.
  const floorLogin = user?.login_type === "password";
  const visibleTabs = TABS.filter((t) => hasPageAccess(t.page));
  const visibleProductionSubtabs = PRODUCTION_SUBTABS.filter((t) => (
    t.anyOf ? t.anyOf.some((k) => hasPageAccess(k)) : hasPageAccess(t.page)
  ));
  const visibleInventorySubtabs = INVENTORY_SUBTABS.filter((t) => (
    t.anyOf ? t.anyOf.some((k) => hasPageAccess(k)) : (!t.page ? !floorLogin : hasPageAccess(t.page))
  ));
  const visibleNpdSubtabs = NPD_SUBTABS.filter((t) => (
    t.anyOf ? t.anyOf.some((k) => hasPageAccess(k)) : hasPageAccess(t.page)
  ));
  const visibleMasterDataSubtabs = MASTER_DATA_SUBTABS.filter((t) => hasPageAccess(t.page));
  const visibleAdministrationSubtabs = ADMINISTRATION_SUBTABS.filter((t) => t.roleOnly ? (user?.role === "super_admin" || user?.role === "admin") : hasPageAccess(t.page));
  const productionActive = PRODUCTION_SUBTABS.some((t) => t.to === pathname);
  const inventoryActive = pathname === "/inventory" || pathname === "/inventory/movement-audit-trail" || pathname === "/inventory/wip-by-stage" || pathname.startsWith("/storeapproval") || pathname === "/physical-inventory";
  const npdActive = pathname.startsWith("/npd-requests") || pathname === "/npd-store-request";
  const masterDataActive = MASTER_DATA_SUBTABS.some((t) => t.to === pathname);
  const administrationActive = ADMINISTRATION_SUBTABS.some((t) => t.to === pathname) || pathname.startsWith("/admin/boo");

  const dropdownGroups = [
    { key: "production", label: "Production", testId: "nav-production", active: productionActive, tabs: visibleProductionSubtabs },
    { key: "inventory", label: "Inventory Management", testId: "nav-inventory", active: inventoryActive, tabs: visibleInventorySubtabs },
    { key: "npd", label: "NPD", testId: "nav-npd", active: npdActive, tabs: visibleNpdSubtabs },
    { key: "master-data", label: "Master Data", testId: "nav-master-data", active: masterDataActive, tabs: visibleMasterDataSubtabs },
    { key: "administration", label: "Administration", testId: "nav-administration", active: administrationActive, tabs: visibleAdministrationSubtabs },
  ];

  return (
    <>
      {/* Desktop / large tablet nav - unchanged pill tabs.
          Sep 15 2026 fix: tabs+dropdowns wrap onto a second line (instead
          of overflowing OR requiring horizontal scroll) when they don't
          fit in one row - user's explicit ask: never require left/right
          scrolling to see the main menu, and never overlap the user-menu
          or SAP/ERP status badges. Each page's <header> uses min-h-16 (was
          a fixed h-16) so it can grow to fit a second line when needed. */}
      <div className="hidden lg:flex items-center gap-1 w-full min-w-0">
      <div className="flex items-center flex-wrap gap-1.5 min-w-0 flex-1 py-1.5">
      {visibleTabs.map((tab) => {
        const active = pathname === tab.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[13px] font-bold font-heading transition-colors duration-150 ${
              active ? "bg-white text-[#0B6B74]" : "text-white/85 hover:bg-white/15 hover:text-white"
            }`}
            data-testid={tab.testId}
          >
            {tab.label}
          </Link>
        );
      })}
      {dropdownGroups.map((group) => group.tabs.length > 0 && (
        <DropdownMenu key={group.key}>
          <DropdownMenuTrigger
            className={`shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-[13px] font-bold font-heading transition-colors duration-150 outline-none ${
              group.active ? "bg-white text-[#0B6B74]" : "text-white/85 hover:bg-white/15 hover:text-white"
            }`}
            data-testid={group.testId}
          >
            {group.label}
            <CaretDown size={10} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[200px] bg-white border border-[#D0D5DD]" data-testid={`${group.testId}-dropdown-content`}>
            {group.tabs.map((tab) => (
              <DropdownMenuItem key={tab.to} asChild>
                <Link to={tab.to} className="w-full cursor-pointer" data-testid={tab.testId}>
                  {tab.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      </div>

      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-bold font-heading text-white/85 hover:bg-white/15 hover:text-white transition-colors duration-150 outline-none shrink-0"
            data-testid="nav-user-menu"
          >
            <UserCircle size={16} weight="fill" />
            {user.name || user.email}
            <CaretDown size={10} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[220px] bg-white border border-[#D0D5DD]" data-testid="nav-user-menu-dropdown-content">
            <div className="px-3 py-2 text-xs text-[#667085] truncate">{user.email || user.username}</div>
            <DropdownMenuSeparator />
            {user.login_type === "password" && (
              <DropdownMenuItem onClick={() => setChangingPassword(true)} className="cursor-pointer flex items-center gap-2" data-testid="nav-change-password">
                <Key size={14} weight="bold" />
                Change Password
              </DropdownMenuItem>
            )}
            {(user.role === "super_admin" || user.role === "admin") && (
              <DropdownMenuItem asChild>
                <Link to="/admin/access-management" className="w-full cursor-pointer flex items-center gap-2" data-testid="nav-access-management">
                  <ShieldCheck size={14} weight="bold" />
                  Access Management
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} className="cursor-pointer flex items-center gap-2 text-[#B42318]" data-testid="nav-sign-out">
              <SignOut size={14} weight="bold" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      </div>

      {/* Mobile / tablet nav - hamburger trigger + off-canvas drawer */}
      <div className="flex lg:hidden items-center ml-auto">
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className="w-11 h-11 flex items-center justify-center rounded-lg text-white hover:bg-white/15 transition-colors duration-150 shrink-0"
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
            >
              <List size={22} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[85vw] max-w-sm p-0 flex flex-col bg-white z-[60]"
            data-testid="mobile-nav-drawer"
          >
            <SheetHeader className="px-4 pt-5 pb-3 border-b border-[#EAECF0] text-left">
              <SheetTitle className="text-[#0E7C86] font-heading">Production Control</SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-2 py-2">
              {visibleTabs.map((tab) => {
                const active = pathname === tab.to;
                return (
                  <SheetClose asChild key={tab.to}>
                    <Link
                      to={tab.to}
                      className={`flex items-center min-h-11 px-3 rounded-lg text-[14px] font-bold font-heading transition-colors duration-150 ${
                        active ? "bg-[#0E7C86]/10 text-[#0B6B74]" : "text-[#344054] hover:bg-slate-100"
                      }`}
                      data-testid={`mobile-${tab.testId}`}
                    >
                      {tab.label}
                    </Link>
                  </SheetClose>
                );
              })}

              {dropdownGroups.some((g) => g.tabs.length > 0) && (
                <Accordion type="multiple" className="mt-1">
                  {dropdownGroups.map((group) => group.tabs.length > 0 && (
                    <AccordionItem key={group.key} value={group.key} className="border-b-0">
                      <AccordionTrigger
                        className={`px-3 min-h-11 text-[14px] font-bold font-heading no-underline hover:no-underline ${
                          group.active ? "text-[#0B6B74]" : "text-[#344054]"
                        }`}
                        data-testid={`mobile-${group.testId}`}
                      >
                        {group.label}
                      </AccordionTrigger>
                      <AccordionContent className="pl-3">
                        {group.tabs.map((tab) => (
                          <SheetClose asChild key={tab.to}>
                            <Link
                              to={tab.to}
                              className="flex items-center min-h-11 px-3 rounded-lg text-[14px] font-medium text-[#475467] hover:bg-slate-100"
                              data-testid={`mobile-${tab.testId}`}
                            >
                              {tab.label}
                            </Link>
                          </SheetClose>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>

            {user && (
              <div className="border-t border-[#EAECF0] px-4 py-3">
                <div className="flex items-center gap-2 mb-2" data-testid="mobile-nav-user-info">
                  <UserCircle size={20} weight="fill" className="text-[#667085]" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[13px] font-bold text-[#1D2939] truncate">{user.name || user.email}</span>
                    <span className="text-[11px] text-[#667085] truncate">{user.email}</span>
                  </div>
                </div>
                {(user.role === "super_admin" || user.role === "admin") && (
                  <SheetClose asChild>
                    <Link
                      to="/admin/access-management"
                      className="flex items-center gap-2 min-h-11 px-3 rounded-lg text-[14px] font-bold text-[#344054] hover:bg-slate-100"
                      data-testid="mobile-nav-access-management"
                    >
                      <ShieldCheck size={16} weight="bold" />
                      Access Management
                    </Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={logout}
                    className="flex items-center gap-2 min-h-11 px-3 rounded-lg text-[14px] font-bold text-[#B42318] hover:bg-red-50 w-full"
                    data-testid="mobile-nav-sign-out"
                  >
                    <SignOut size={16} weight="bold" />
                    Sign Out
                  </button>
                </SheetClose>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
      <ChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
    </>
  );
};
